import React, { useState, useRef, useEffect } from 'react';
import './ModernDropdown.css';

const ModernDropdown = ({
  options = [],
  value = '',
  onChange,
  placeholder = 'Select an option',
  name,
  className = '',
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Ensure options is an array
  const optionsArray = Array.isArray(options) ? options : [];

  // Filter options based on search term
  const filteredOptions = optionsArray.filter(option =>
    option.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get selected option
  const selectedOption = optionsArray.find(option => option.id === value);

  // Handle option selection
  const handleOptionClick = (option) => {
    onChange({ target: { name, value: option.id } });
    setIsOpen(false);
    setSearchTerm('');
  };

  // Handle input focus (removed auto-open on focus to prevent double toggle)
  const handleInputFocus = () => {
    // Intentionally empty to prevent focus from toggling state
  };

  // Handle input click
  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setTimeout(() => inputRef.current?.focus(), 0);
      }
    }
  };

  // Handle input change
  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  // Handle input key down
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredOptions.length === 1) {
        handleOptionClick(filteredOptions[0]);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`modern-dropdown ${className} ${disabled ? 'disabled' : ''}`} ref={dropdownRef}>
      <div className="dropdown-input-container" onClick={handleInputClick}>
        <input
          ref={inputRef}
          type="text"
          value={isOpen ? searchTerm : (selectedOption?.name || '')}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          className="dropdown-input"
          disabled={disabled}
          readOnly={!isOpen}
        />
        <div className="dropdown-arrow">
          <svg
            width="16"
            height="16"
            viewBox="0 0 20 20"
            fill="none"
            className={`arrow-icon ${isOpen ? 'open' : ''}`}
          >
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.5"
              d="m6 8 4 4 4-4"
            />
          </svg>
        </div>
      </div>

      {isOpen && (
        <div className="dropdown-menu">
          <div className="dropdown-options">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <div
                  key={option.id}
                  className={`dropdown-option ${value === option.id ? 'selected' : ''}`}
                  onClick={() => handleOptionClick(option)}
                >
                  {option.name}
                </div>
              ))
            ) : (
              <div className="dropdown-option no-results">
                No departments found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ModernDropdown;
